import {useLocation, useParams} from "react-router-dom";
import {Badge, Button, Col, Input, Row, Space, Spin, Table, TableProps, Tooltip} from "antd";
import React, {useEffect, useState} from "react";
import {UtilsService} from "../../../services/Utils.service";
import {
    ArrowRightOutlined,
    CheckOutlined,
    DownloadOutlined,
    EyeOutlined,
    MinusOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {HttpService} from "../../../services/http/Http.service";
import DetailModal from "../workOnPractices/DetailModal";
import {PracticeExportService} from "../PracticeExport.service";
import Title from "antd/es/typography/Title";
import {SelectAllButton} from "../SelectAllButton";
import {SimulatedFileTS, StatusCodeE} from "../../../model/models";
import {TemplateUtils} from "../../../services/utils/TemplateUtils";
import {combinatorColumns} from "../combinatorColumns";
import {usePaginatedData} from "../../../services/hooks/usePaginatedData";
import dayjs from "dayjs";
import UserDetailsButton from "../../userDetails/UserDetailsButton";

export default function WaitingPaymentPractices() {
    const {id} = useParams();
    const location = useLocation();

    // Use the custom hook for table data management
    const {
        data: filteredData,
        totalItems,
        pageData,
        searchText,
        setSearchText,
        handleTableChange,
    } = usePaginatedData<SimulatedFileTS>({
        simulatorId: id,
        status: StatusCodeE.ATTESA_PAGAMENTO,
        initialPageSize: 20
    });

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [combinatorData, setCombinatorData] = useState(location.state?.simulatorData);
    const [selectedTotal, setSelectedTotal] = useState(0);
    const [isOpen, setIsOpen] = useState(false)
    const [userId, setUserId] = useState<number>()
    const [username, setUsername] = useState<string>("")
    const [fiscalCode, setFiscalCode] = useState<string>("")
    const [type, setType] = useState<string>("")
    const [isRateApplied, setIsRateApplied] = useState<boolean>(false)
    const [loadingPersonId, setLoadingPersonId] = useState<number | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (!combinatorData) {
            HttpService.getInstanceAxios().get(`/simulazione/${id}/statistiche`)
                .then(response => {
                    const item = response.data || {};

                    const { simulator, statistics } = item;
                    const { filters } = simulator;

                    let yearLabel = filters.years.join(" - ");
                    if (filters.checked) {
                        yearLabel += " (Anni indicati)";
                    }

                    let exemptionLabel = "Tutti";
                    if (filters.exemptionCodes?.length > 0) {
                        exemptionLabel = filters.exemptionCodes.join(" - ");
                    }
                    if (filters.exemptionCodeChecked) {
                        exemptionLabel += " (Codici indicati)";
                    }
                    setCombinatorData([{
                        key: simulator.id,
                        numberPositions: statistics.totalOccurrences,
                        numberAssisted: statistics.totalPerson,
                        exemptionCode: exemptionLabel,
                        year: yearLabel,
                        type: statistics.typeList?.join(" - "),
                        amount: "€ " + UtilsService.formatAmountRange([filters.minAmount, filters.maxAmount]),
                        ticket: "€ " + UtilsService.formatNumber(statistics.totalTicket),
                    }]);
                })
        }
    }, [combinatorData]);

    useEffect(() => {
        if (userId !== null && userId !== undefined) {
            setIsOpen(true);
        }
    }, [userId]);

    const rowSelection: TableProps<SimulatedFileTS>['rowSelection'] = {
        selectedRowKeys: selectedRowKeys,
        onChange: (selectedRowKeys: React.Key[], selectedRows: SimulatedFileTS[]) => {
            const total = selectedRows.reduce((sum, row) => sum + row.totalSum, 0);
            setSelectedTotal(total);
            setSelectedRowKeys(selectedRowKeys)
        },

    };

    async function exportToExcel(xlsx: string) {
        if (!id) return;
        setIsExporting(true);
        try {
            await PracticeExportService.loadAndExportToExcel(Number.parseInt(id), StatusCodeE.ATTESA_PAGAMENTO, xlsx);
        } finally {
            setIsExporting(false);
        }
    }

    const handleModal = (record: SimulatedFileTS) => {
        setUserId(record.personId)
        setUsername(record.fullName)
        setFiscalCode(record.fiscalCode)
        setType(record.type)
        setIsRateApplied(record.hasInstallment)
    }

    const columns = [
        {
            title: "Codice Fiscale",
            dataIndex: "fiscalCode",
            key: "fiscalCode",
            sorter: (a: any, b: any) => a.fiscalCode.localeCompare(b.fiscalCode),
        },
        {
            title: "Cognome Nome",
            dataIndex: "fullName",
            key: "fullName",
            sorter: (a: any, b: any) => a.fullName.localeCompare(b.fullName),
        },
        {
            title: "Anno",
            dataIndex: "years",
            key: "years",
            render: (years: string[]) => years.sort((a, b) => parseInt(a) - parseInt(b)).join(" - "),
        },
        {
            title: "Codici esenzione",
            dataIndex: "exemptionCodes",
            key: "exemptionCodes",
            sorter: (a: any, b: any) => a.exemptionCodes.join(", ").localeCompare(b.exemptionCodes.join(", ")),
            render: (codes: string[]) => codes.join(" - "),
        },
        {
            title: "Tipo",
            dataIndex: "type",
            key: "type"
        },
        {
            title: "Rate",
            dataIndex: "hasInstallment",
            key: "hasInstallment",
            render: (value: boolean) => (value ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "PagoPA",
            dataIndex: "hasPayment",
            key: "hasPayment",
            render: (value: boolean) => (value ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "Interessi",
            dataIndex: "totalInterest",
            key: "totalInterest",
            render: (value: number) => (value > 0 ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "Importo totale",
            dataIndex: "totalSum",
            key: "totalSum",
            sorter: (a: any, b: any) => (a.totalSum ?? 0) - (b.totalSum ?? 0),
            render: (value: number) => "€ " + UtilsService.formatNumber(value),
        },
        {
            title: "No. Protocollo",
            dataIndex: "protocolNumbers",
            key: "protocolNumbers",
            render:  ((protocolNumbers: string[]) => protocolNumbers.join(" - "))
        },
        {
            title: "Data di notifica",
            dataIndex: "communicationDate",
            key: "communicationDate",
            render: (val: string) => val ? dayjs(val).format("DD/MM/YYYY") : "—"
        },
        {
            title: "Azioni",
            key: "operation",
            render: (record: any) => (
                <Space>
                    <Tooltip placement="top" title={'Visualizza Glifo generato'}>
                        <Button
                            onClick={() => {
                                setLoadingPersonId(record.personId);
                                TemplateUtils.downloadGlyphedDocument(
                                    combinatorData[0].key,
                                    record.personId,
                                    () => setLoadingPersonId(null)
                                );
                            }}
                            icon={
                                loadingPersonId === record.personId ? <Spin size="small"/> : <EyeOutlined/>
                            }
                            disabled={loadingPersonId === record.personId}
                        />
                    </Tooltip>
                    <Tooltip placement="top" title={'Scarica file firmato'}>
                        <Button onClick={() => TemplateUtils.downloadSignedDocument(combinatorData[0].key, record.personId)}
                                icon={<DownloadOutlined/>}/>
                    </Tooltip>
                    <Button onClick={() => handleModal(record)} type={"primary"} icon={<ArrowRightOutlined/>}/>
                    <UserDetailsButton personID={record.personId} combinatorID={Number(id)}></UserDetailsButton>
                </Space>
            ),
        },
    ];



    return (
        <>
            <Row justify="space-between" align="middle">
                <Col>
                    <Input
                        style={{width: "400px"}}
                        size="middle"
                        allowClear
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        addonBefore={<SearchOutlined/>}
                        placeholder="Nome, Cognome, Codice Fiscale"
                    />
                </Col>
                <Col>
                    <Space>
                        <Button
                            className="custom-label-color"
                            type="primary"
                            icon={<DownloadOutlined/>}
                            loading={isExporting}
                            onClick={() => exportToExcel("Report.xlsx")}
                        >
                            Esporta
                        </Button>
                    </Space>
                </Col>
            </Row>
            <Space size={30} direction={"vertical"} style={{width: "100%"}}>
                <Table
                    columns={combinatorColumns}
                    dataSource={combinatorData}
                    pagination={false}
                    bordered
                />
                <Space style={{display: "flex", justifyContent: "space-between"}}>
                    {id && <SelectAllButton
                        idSimulator={id}
                        status={StatusCodeE.ATTESA_PAGAMENTO}
                        onSelected={(keys) => {
                            setSelectedRowKeys(keys);
                            const keyNumbers = keys.map(k => Number(k));
                            const total = filteredData
                                .filter(row => keyNumbers.includes(row.personId))
                                .reduce((sum, row) => sum + (row.totalSum || 0), 0);

                            setSelectedTotal(total);
                        }}
                    />

                    }
                    <div>
                        Utenti selezionati: <Badge color={"#007A33FF"}
                                                   count={selectedRowKeys ? selectedRowKeys.length : 0} showZero={true}
                                                   overflowCount={9999}
                                                   style={{fontSize: "14px", transform: "scale(1.1)"}}/>
                    </div>
                </Space>
                <Table<SimulatedFileTS>
                    rowSelection={{ type: "checkbox", ...rowSelection }}
                    rowKey={record => record.personId}
                    dataSource={filteredData}
                    columns={columns}
                    pagination={{
                        total: totalItems,
                        showTotal: (total) => `Totale ${total} elementi`,
                        current: pageData.current,
                        pageSize: pageData.pageSize,
                        pageSizeOptions: ["10", "20", "50", "100", "250", "500"],
                        showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                />
            </Space>
            <Title className={"custom-title"} level={5}>Importo selezionato:
                € {UtilsService.formatNumber(selectedTotal)}</Title>
            {isOpen ?
                <DetailModal isOpen={isOpen}
                             combinatorId={id}
                             setIsOpen={setIsOpen}
                             userId={userId}
                             setUserId={setUserId}
                             username={username}
                             fiscalCode={fiscalCode}
                             type={type}
                             isRateApplied={isRateApplied}
                />
                : null
            }
        </>
    )
}